import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { buildDisplayName, PatientNameParts } from '../types';
import { CALENDAR_TIME_ZONE, getZonedDate, makeDateInTimeZone } from '../components/calendar/utils';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate } from '../utils/dateFormat';
import { sessionClaims } from '../utils/session';
import './HomeDashboard.css';

type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'arrived'
  | 'seated'
  | 'ready_for_checkout'
  | 'completed'
  | 'canceled';

type RawAppointment = {
  id: number;
  patientId?: number | null;
  prospectName?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  duration?: number;
  reason?: string | null;
  customReason?: string | null;
  treatmentTemplate?: { name?: string | null } | null;
  treatment?: { name?: string | null } | null;
  notes?: string | null;
  color?: string | null;
  doctorId?: number | null;
  secondDoctorId?: number | null;
  doctor?: { firstName?: string; lastName?: string } | null;
  secondDoctor?: { firstName?: string; lastName?: string } | null;
  patient?: (Partial<PatientNameParts> & { name?: string; presentedName?: string }) | null;
  status?: AppointmentStatus | null;
  arrivedAt?: string | null;
  checkoutNote?: string | null;
  paymentStatus?: string;
  room?: number | null;
  isAllDay?: boolean;
  isNoShow?: boolean;
  canceledAt?: string | null;
};

type RawPatient = Partial<PatientNameParts> & {
  id: number;
  email?: string;
  phoneNumber?: string;
  presentedName?: string;
  name?: string;
  notes?: string | null;
  hasCriticalAllergy?: boolean;
  balanceDue?: number;
};

type RawDoctor = {
  id: number;
  firstName?: string;
  lastName?: string;
};

type TimelineEntry = {
  id: number;
  patientId: number | null;
  patientName: string;
  email?: string;
  phoneNumber?: string;
  patientNotes: string;
  hasCriticalAllergy: boolean;
  balanceDue: number;
  startTime: string;
  endTime: string;
  reason: string;
  appointmentNotes: string;
  doctorNames: string[];
  color?: string | null;
  status: AppointmentStatus;
  arrivedAt?: string | null;
  checkoutNote?: string | null;
  room?: number | null;
  isAllDay: boolean;
  isNoShow: boolean;
  operationEligible: boolean;
};

interface HomeDashboardProps {
  token: string;
  onOpenPatient: (patientId: number) => void;
  onShowCalendar: () => void;
}

const terminalStatuses = new Set<AppointmentStatus>(['completed', 'canceled']);

export const getClinicDayRange = (reference: Date) => {
  const start = makeDateInTimeZone(reference, 0, 0, CALENDAR_TIME_ZONE);
  return {
    start,
    end: new Date(start.getTime() + 24 * 60 * 60 * 1000)
  };
};

const formatLocalTime = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: CALENDAR_TIME_ZONE
  }).format(new Date(iso));

const coercePatientName = (
  raw: Partial<PatientNameParts> & { presentedName?: string; id?: number; name?: string },
  t: (key: string) => string
) => {
  const derived = buildDisplayName(raw);
  if (derived && derived.trim().length > 0) return derived;
  if (raw.name && raw.name.trim().length > 0) return raw.name;
  return raw.id ? `${t('dashboard.patientPrefix')}${raw.id}` : t('dashboard.unnamedPatient');
};

const coerceDoctorName = (doctor?: { firstName?: string; lastName?: string } | null) =>
  [doctor?.firstName, doctor?.lastName].filter(Boolean).join(' ').trim();

const parseRole = (_token: string) => sessionClaims().role.toLowerCase();

export default function HomeDashboard({ token, onOpenPatient, onShowCalendar }: HomeDashboardProps) {
  const { t, i18n } = useTranslation();
  const theme = useThemedStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [actionBusyId, setActionBusyId] = useState<number | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(() => new Set());
  const [now, setNow] = useState(() => new Date());
  const role = useMemo(() => parseRole(token), [token]);

  useEffect(() => {
    let cancelled = false;

    const load = async (initial: boolean) => {
      if (initial) setLoading(true);
      try {
        const day = getClinicDayRange(new Date());
        const appointmentUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS)}?start=${encodeURIComponent(day.start.toISOString())}&end=${encodeURIComponent(day.end.toISOString())}`;
        const [appointmentsRes, patientsRes, doctorsRes, operationsRes] = await Promise.all([
          fetch(appointmentUrl, { headers: getAuthHeaders(token) }),
          fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), { headers: getAuthHeaders(token) }),
          fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) }),
          fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/operations/today`), { headers: getAuthHeaders(token) })
        ]);

        if (!appointmentsRes.ok) throw new Error(t('dashboard.loadErrorAppointments'));
        if (!patientsRes.ok) throw new Error(t('dashboard.loadErrorPatients'));
        if (!doctorsRes.ok) throw new Error(t('dashboard.loadErrorDoctors'));
        if (!operationsRes.ok) throw new Error(t('dashboard.loadErrorAppointments'));

        const [appointments, patients, doctors, operations]: [
          RawAppointment[],
          RawPatient[],
          RawDoctor[],
          RawAppointment[]
        ] = await Promise.all([
          appointmentsRes.json(),
          patientsRes.json(),
          doctorsRes.json(),
          operationsRes.json()
        ]);

        if (cancelled) return;

        const patientLookup = new Map<number, RawPatient>();
        patients.forEach(patient => patientLookup.set(patient.id, patient));

        const doctorLookup = new Map<number, string>();
        doctors.forEach(doctor => doctorLookup.set(doctor.id, coerceDoctorName(doctor)));

        const operationLookup = new Map<number, RawAppointment>();
        operations.forEach(operation => operationLookup.set(operation.id, operation));

        const timeline = appointments
          .filter(appointment => {
            if (!appointment?.startTime) return false;
            const start = new Date(appointment.startTime);
            return start >= day.start && start < day.end;
          })
          .map((appointment): TimelineEntry => {
            const operation = operationLookup.get(appointment.id);
            const patientId = typeof appointment.patientId === 'number' ? appointment.patientId : null;
            const patient = patientId ? patientLookup.get(patientId) : undefined;
            const patientName = patient
              ? coercePatientName(patient, t)
              : appointment.patient
                ? coercePatientName({ ...appointment.patient, id: patientId ?? undefined }, t)
                : appointment.prospectName?.trim() || t('dashboard.unnamedPatient');

            const doctorNames = [
              coerceDoctorName(appointment.doctor) || (appointment.doctorId ? doctorLookup.get(appointment.doctorId) : ''),
              coerceDoctorName(appointment.secondDoctor) || (appointment.secondDoctorId ? doctorLookup.get(appointment.secondDoctorId) : '')
            ].filter((name): name is string => !!name);

            const startTime = appointment.startTime!;
            const fallbackEnd = new Date(
              new Date(startTime).getTime() + Math.max(appointment.duration || 15, 15) * 60_000
            ).toISOString();

            return {
              id: appointment.id,
              patientId,
              patientName,
              email: patient?.email,
              phoneNumber: patient?.phoneNumber,
              patientNotes: patient?.notes?.trim() || '',
              hasCriticalAllergy: !!patient?.hasCriticalAllergy,
              balanceDue: typeof patient?.balanceDue === 'number' ? patient.balanceDue : 0,
              startTime,
              endTime: appointment.endTime || fallbackEnd,
              reason: appointment.treatment?.name
                || appointment.treatmentTemplate?.name
                || appointment.customReason
                || appointment.reason
                || t('dashboard.defaultReason'),
              appointmentNotes: appointment.notes?.trim() || '',
              doctorNames,
              color: appointment.color ?? null,
              status: operation?.status || appointment.status || 'scheduled',
              arrivedAt: operation?.arrivedAt ?? appointment.arrivedAt,
              checkoutNote: operation?.checkoutNote ?? appointment.checkoutNote,
              room: appointment.room,
              isAllDay: !!appointment.isAllDay,
              isNoShow: !!appointment.isNoShow,
              operationEligible: operationLookup.has(appointment.id)
            };
          })
          .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

        setEntries(timeline);
        setError(null);
        setRefreshError(null);
      } catch (loadError) {
        if (cancelled) return;
        const message = loadError instanceof Error ? loadError.message : t('dashboard.loadErrorDashboard');
        if (initial) setError(message);
        else setRefreshError(message);
      } finally {
        if (initial && !cancelled) setLoading(false);
      }
    };

    void load(true);
    const refreshInterval = window.setInterval(() => void load(false), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshInterval);
    };
  }, [t, token]);

  useEffect(() => {
    const clockInterval = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(clockInterval);
  }, []);

  const transitionOperation = async (
    appointment: TimelineEntry,
    targetStatus: 'seated' | 'ready_for_checkout'
  ) => {
    let checkoutNote: string | null = null;
    if (targetStatus === 'ready_for_checkout') {
      const prompted = window.prompt(t('calendar.lifecycle.checkoutNotePlaceholder'));
      if (prompted === null) return;
      checkoutNote = prompted.trim() || null;
    }

    setActionBusyId(appointment.id);
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/lifecycle`),
        {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify({ targetStatus, checkoutNote })
        }
      );
      if (!response.ok) throw new Error(await response.text());
      const updated = await response.json();
      setEntries(current => current.map(item =>
        item.id === appointment.id ? { ...item, ...updated, operationEligible: true } : item
      ));
    } catch (actionError) {
      setRefreshError(actionError instanceof Error ? actionError.message : t('dashboard.loadErrorDashboard'));
    } finally {
      setActionBusyId(null);
    }
  };

  const toggleNote = (key: string) => {
    setExpandedNotes(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const heading = useMemo(() => formatDate(getZonedDate(now)), [now]);

  const summary = useMemo(() => {
    const currentTime = now.getTime();
    const remaining = entries.filter(entry =>
      !terminalStatuses.has(entry.status) && new Date(entry.endTime).getTime() >= currentTime
    ).length;
    const waiting = entries.filter(entry => entry.status === 'arrived' || entry.status === 'seated').length;
    const allergyPatients = new Set(entries.filter(entry => entry.hasCriticalAllergy).map(entry => entry.patientId ?? `appointment-${entry.id}`));
    const debtPatients = new Set(entries.filter(entry => entry.balanceDue > 0.005).map(entry => entry.patientId ?? `appointment-${entry.id}`));
    return {
      total: entries.length,
      remaining,
      waiting,
      allergies: allergyPatients.size,
      debts: debtPatients.size
    };
  }, [entries, now]);

  const timelineRows = useMemo(() => {
    const rows: Array<{ kind: 'now'; key: string } | { kind: 'appointment'; entry: TimelineEntry }> = [];
    let nowInserted = false;
    const nowTime = now.getTime();
    entries.forEach(entry => {
      if (!nowInserted && nowTime < new Date(entry.startTime).getTime()) {
        rows.push({ kind: 'now', key: 'now' });
        nowInserted = true;
      }
      rows.push({ kind: 'appointment', entry });
    });
    if (!nowInserted) rows.push({ kind: 'now', key: 'now' });
    return rows;
  }, [entries, now]);

  const pageVariables = {
    '--dashboard-bg': theme.tokens.app.background,
    '--dashboard-surface': theme.tokens.app.surface,
    '--dashboard-surface-alt': theme.tokens.app.surfaceAlt,
    '--dashboard-subtle': theme.tokens.app.subtle,
    '--dashboard-input': theme.tokens.app.input,
    '--dashboard-border': theme.tokens.app.border,
    '--dashboard-text': theme.tokens.app.text,
    '--dashboard-muted': theme.tokens.app.muted,
    '--dashboard-primary': theme.tokens.app.primary,
    '--dashboard-primary-text': theme.tokens.app.primaryText,
    '--dashboard-shadow': theme.tokens.app.shadow
  } as React.CSSProperties;

  if (loading) {
    return <div className="home-dashboard" style={pageVariables}>{t('dashboard.loading')}</div>;
  }

  if (error) {
    return <div className="home-dashboard home-dashboard-error" style={pageVariables}>{error}</div>;
  }

  if (entries.length === 0) {
    return (
      <div className="home-dashboard" style={pageVariables}>
        <div className="home-dashboard-empty">
          <h2>{t('dashboard.noAppointments')}</h2>
          <p>{t('dashboard.noAppointmentsDesc')}</p>
          <button className="dashboard-button dashboard-button-secondary" onClick={onShowCalendar}>
            {t('dashboard.openCalendar')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="home-dashboard" style={pageVariables}>
      <header className="home-dashboard-header">
        <div>
          <h1>{t('dashboard.todayTimeline')}</h1>
          <div className="home-dashboard-date">{heading}</div>
        </div>
        <button className="dashboard-button dashboard-button-secondary" onClick={onShowCalendar}>
          {t('dashboard.openCalendar')}
        </button>
      </header>

      {refreshError && (
        <div className="home-dashboard-refresh-error" role="alert">
          {t('dashboard.refreshError')}: {refreshError}
        </div>
      )}

      <section className="dashboard-summary" aria-label={t('dashboard.summary')}>
        {[
          [t('dashboard.totalAppointments'), summary.total],
          [t('dashboard.remainingAppointments'), summary.remaining],
          [t('dashboard.waitingPatients'), summary.waiting],
          [t('dashboard.allergyAlerts'), summary.allergies],
          [t('dashboard.debtAlerts'), summary.debts]
        ].map(([label, value]) => (
          <div className="dashboard-summary-card" key={String(label)}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </section>

      <section className="appointment-timeline" aria-label={t('dashboard.todayTimeline')}>
        {timelineRows.map(row => {
          if (row.kind === 'now') {
            return (
              <div className="timeline-now" key={row.key} data-testid="timeline-now">
                <time>{formatLocalTime(now.toISOString(), i18n.language)}</time>
                <span aria-hidden="true" />
                <strong>{t('dashboard.now')}</strong>
              </div>
            );
          }

          const entry = row.entry;
          const start = new Date(entry.startTime);
          const end = new Date(entry.endTime);
          const active = !terminalStatuses.has(entry.status) && now >= start && now <= end;
          const past = end < now;
          const subdued = terminalStatuses.has(entry.status);
          const waitingMinutes = entry.status === 'arrived' && entry.arrivedAt
            ? Math.max(0, Math.floor((now.getTime() - new Date(entry.arrivedAt).getTime()) / 60_000))
            : null;
          const canSeat = entry.operationEligible
            && (role === 'doctor' || role === 'admin')
            && entry.status === 'arrived';
          const canReady = entry.operationEligible
            && (role === 'doctor' || role === 'admin')
            && entry.status === 'seated';
          const appointmentNoteKey = `${entry.id}:appointment`;
          const patientNoteKey = `${entry.id}:patient`;

          return (
            <Fragment key={entry.id}>
              <article
                className={[
                  'timeline-entry',
                  active ? 'is-active' : '',
                  past ? 'is-past' : '',
                  subdued ? 'is-subdued' : ''
                ].filter(Boolean).join(' ')}
                style={{ '--appointment-color': entry.color || theme.primary } as React.CSSProperties}
                data-status={entry.status}
              >
                <div className="timeline-time">
                  <time dateTime={entry.startTime}>{formatLocalTime(entry.startTime, i18n.language)}</time>
                  <span>{formatLocalTime(entry.endTime, i18n.language)}</span>
                </div>
                <div className="timeline-rail" aria-hidden="true"><span /></div>
                <div className="timeline-card">
                  <div className="timeline-card-header">
                    <div>
                      <h2>{entry.patientName}</h2>
                      <div className="timeline-reason">{entry.reason}</div>
                    </div>
                    <span className={`timeline-status status-${entry.status}`}>
                      {t(`calendar.lifecycle.status.${entry.status}`)}
                    </span>
                  </div>

                  <div className="timeline-meta">
                    {entry.doctorNames.length > 0 && (
                      <span>{t('dashboard.withDoctor', { doctor: entry.doctorNames.join(' / ') })}</span>
                    )}
                    {entry.room && <span>{t('dashboard.room', { room: entry.room })}</span>}
                    {entry.phoneNumber && <span>{entry.phoneNumber}</span>}
                    {entry.email && <span>{entry.email}</span>}
                    {entry.isNoShow && <span className="timeline-flag timeline-flag-danger">{t('calendar.markedAsNoShow')}</span>}
                  </div>

                  <div className="timeline-alerts">
                    {entry.hasCriticalAllergy && (
                      <strong className="timeline-flag timeline-flag-danger">{t('dashboard.criticalAllergy')}</strong>
                    )}
                    {entry.balanceDue > 0.005 && (
                      <strong className="timeline-flag timeline-flag-debt">
                        {t('dashboard.patientDebt')}: Bs {entry.balanceDue.toFixed(2)}
                      </strong>
                    )}
                    {waitingMinutes !== null && (
                      <strong className="timeline-flag timeline-flag-waiting">
                        {t('calendar.lifecycle.waitingMinutes', { count: waitingMinutes })}
                      </strong>
                    )}
                  </div>

                  {(entry.appointmentNotes || entry.patientNotes || entry.checkoutNote) && (
                    <div className="timeline-notes">
                      {entry.appointmentNotes && (
                        <div className="timeline-note">
                          <strong>{t('dashboard.appointmentNotes')}</strong>
                          <p className={expandedNotes.has(appointmentNoteKey) ? 'is-expanded' : ''}>{entry.appointmentNotes}</p>
                          {entry.appointmentNotes.length > 120 && (
                            <button
                              type="button"
                              aria-expanded={expandedNotes.has(appointmentNoteKey)}
                              onClick={() => toggleNote(appointmentNoteKey)}
                            >
                              {expandedNotes.has(appointmentNoteKey) ? t('dashboard.showLess') : t('dashboard.showMore')}
                            </button>
                          )}
                        </div>
                      )}
                      {entry.patientNotes && (
                        <div className="timeline-note">
                          <strong>{t('dashboard.patientNotes')}</strong>
                          <p className={expandedNotes.has(patientNoteKey) ? 'is-expanded' : ''}>{entry.patientNotes}</p>
                          {entry.patientNotes.length > 120 && (
                            <button
                              type="button"
                              aria-expanded={expandedNotes.has(patientNoteKey)}
                              onClick={() => toggleNote(patientNoteKey)}
                            >
                              {expandedNotes.has(patientNoteKey) ? t('dashboard.showLess') : t('dashboard.showMore')}
                            </button>
                          )}
                        </div>
                      )}
                      {entry.checkoutNote && (
                        <div className="timeline-note timeline-checkout-note">
                          <strong>{t('calendar.lifecycle.checkoutNote')}</strong>
                          <p className="is-expanded">{entry.checkoutNote}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="timeline-actions">
                    {(canSeat || canReady) && (
                      <button
                        className="dashboard-button dashboard-button-primary"
                        disabled={actionBusyId === entry.id}
                        onClick={() => transitionOperation(entry, canSeat ? 'seated' : 'ready_for_checkout')}
                      >
                        {canSeat ? t('calendar.lifecycle.seated') : t('calendar.lifecycle.readyForCheckout')}
                      </button>
                    )}
                    <button className="dashboard-button dashboard-button-secondary" onClick={onShowCalendar}>
                      {entry.status === 'ready_for_checkout'
                        ? t('calendar.lifecycle.checkout')
                        : t('dashboard.openCalendar')}
                    </button>
                    {entry.patientId !== null && (
                      <button
                        className="dashboard-button dashboard-button-primary"
                        onClick={() => onOpenPatient(entry.patientId!)}
                      >
                        {t('dashboard.viewPatient')}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            </Fragment>
          );
        })}
      </section>
    </div>
  );
}
